import React, { useState, useEffect, useCallback } from 'react';
import { Stack, Typography, Divider, IconButton, Paper, Grid, Box, Tooltip, Button
} from '@mui/material';
import { useSnackbar } from 'notistack';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FullScreenModal from 'components/FullScreenModal';
import useConfirm from 'core/hooks/useConfirm';
import EditProperty from './edit';
import EditIcon from '@mui/icons-material/Edit';
import { deleteLibrary, getLibraries } from 'services/libraries';
import ReactDataGrid from '@inovua/reactdatagrid-community'
import '@inovua/reactdatagrid-community/index.css'
import DownloadIcon from '@mui/icons-material/Download';
import { downloadBlob } from 'components/reactdata-grid';

const ApprovedLibraries = () => {
  const gridStyle = { minHeight: 480 }
  const [approvedLibrariesData, setApprovedLibrariesData] = useState([]);
  const [gridRef, setGridRef] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();
  const [libraryInfo, setLibraryInfo] = useState({ id: null });

  const columns = [
    { name: 'name', header: 'Name', width: 240 },
    { name: 'city', header: 'City', width: 150, render: ({data} ) => data?.address?.city || ''},
    { name: 'area', header: 'Area', width: 200, render: ({data} ) => data?.address?.area || ''},
    { name: 'address', header: 'Address', width: 450, render: ({data} ) => {
      const addressArr = [];
      addressArr.push(data?.address?.area);
      addressArr.push(data?.address?.city);
      addressArr.push(data?.address?.state);
      addressArr.push(data?.address?.country);
      addressArr.push(data?.address?.pin);
      return (addressArr.filter((ele)=> !!ele ).join(', ') || ''); 
    }},
    { name: 'createdOn', header: 'Created At', width: 120 },
    { name: 'actions', header: 'Actions', width: 150, render: ({data}) => {
      return (
        <Stack direction="row" spacing={2}>
          <Box>
            <Tooltip title="Edit Library Details" arrow placement="left">
              <IconButton onClick={(e) => onActionBtnClick(e, 'edit', data.libraryId)}>
                <EditIcon color="warning" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box>
            <Tooltip title="Delete Library" arrow placement="left">
              <IconButton onClick={(e) => onActionBtnClick(e, 'delete', data.libraryId)} >
                <DeleteForeverIcon color="error" />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
      )
    } },
  ];

  const defaultFilterValue = [
    { name: 'name', operator: 'contains', type: 'string', value: '' },
    { name: 'city', operator: 'contains', type: 'string', value: '', fn: ({ value, filterValue, data }) => {
      if(filterValue === null){
        return true;
      };
      return data?.address?.city?.toLowerCase()?.includes(filterValue.toLowerCase());
    }},
    { name: 'address', operator: 'contains', type: 'string', value: '', fn: ({ value, filterValue, data }) => {
      if(filterValue === null){
        return true;
      };
      const addressArr = [];
      addressArr.push(data?.address?.area);
      addressArr.push(data?.address?.city);
      addressArr.push(data?.address?.state);
      addressArr.push(data?.address?.country);
      addressArr.push(data?.address?.pin);
      const address = (addressArr.filter((ele)=> !!ele ).join(', ') || ''); 

      return address.toLowerCase().includes(filterValue.toLowerCase());
    }},
    { name: 'area', operator: 'contains', type: 'string', value: '', fn: ({ value, filterValue, data }) => {
      if(filterValue === null){
        return true;
      };
      return data?.address?.area?.toLowerCase()?.includes(filterValue.toLowerCase());
    }},
    { name: 'createdOn', operator: 'contains', type: 'string', value: '' },
  ];

  useEffect(() => {
    getApprovalList();
  }, []);

  function getApprovalList() {
    getLibraries().then(({response, error}) => {
      if(response){
        setApprovedLibrariesData([...(response || [])]);
      } else {
        setApprovedLibrariesData([]);
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  function onActionBtnClick(e, type, libraryId) {
    e.preventDefault();
    if(type === 'edit'){
      editLibraryDetails(libraryId);
    }else if(type === 'delete') {
      onDeleteLibrary(libraryId);
    }
  }

  const onDeleteLibrary = (libraryId) => {
    confirm({ title: 'Delete Library?', description: 'This library will be deleted and this cannot be undone. Are you sure you want to proceed?' })
    .then(() => { 
      deleteProperty(libraryId);
    })
    .catch(() => { 
      
    });
  }

  const deleteProperty = (libraryId) => {
    deleteLibrary({libraryId}).then(({response, error}) => {
      if(response){
        enqueueSnackbar("Library deleted successfully", {variant: 'success?'});
        getApprovalList();
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  const onModalClose = useCallback(
    () => {
      setOpenModal(false);
    },
    [setOpenModal],
  )

 
  const onSuccess = useCallback(
    () => {
      getApprovalList();
      setOpenModal(false);
      setLibraryInfo({id: null});
    },
    [setOpenModal],
  )


  const editLibraryDetails = (libraryId) => {
    setLibraryInfo({ id: libraryId });
    setOpenModal(true);
  }

  const reactDataGridExportCSV = (gridRef, fileName) => {
    const SEPARATOR = ',';

    const columns = gridRef.current.visibleColumns;
  
    const header = columns.map((c) => c.header).filter(header => header !== 'Actions').join(SEPARATOR);
    const rows = gridRef.current.data.map((data) => columns.filter(c => c.id !== 'actions' ).map((c) => {
      if(c.id === 'city') return data?.address?.city || '';
      if(c.id === 'area') return data?.address?.area || '';
      if(c.id === 'address') {
        const addressArr = [];
        addressArr.push(data?.address?.area);
        addressArr.push(data?.address?.city);
        addressArr.push(data?.address?.state);
        addressArr.push(data?.address?.country);
        addressArr.push(data?.address?.pin);
        return `"${(addressArr.filter((ele)=> !!ele ).join(', ') || '')}"`; 
      }
      return ((data[c.id] === undefined || data[c.id] === null) ? '' : data[c.id]).toString().replace('true', 'Active').replace('false', 'Inactive')
    }).join(SEPARATOR));
    const contents = [header].concat(rows).join('\n');
    const blob = new Blob([contents], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, fileName);
  };

  return (
    <Paper sx={{ minWidth: 275, py: 2, px: 3, minHeight: "calc(100vh - 100px )" }}>
    {/* <CardContent> */}
      <Typography component="h2" variant="h6" color="secondary" gutterBottom sx={{mb: 1}}>
        Approved Libraries
      </Typography>
      <Divider />
      <Box noValidate  sx={{ mt: 2 }} >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12} lg={12} textAlign="right" >
            <Button onClick={() => reactDataGridExportCSV(gridRef, 'approved-libraries.csv') } startIcon={<DownloadIcon />} variant="outlined" color="success" sx={{textTransform: 'none'}}>Export CSV</Button>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} >
            <Box component="div" sx={{   maxHeight: 720, width: '100%' }}>
              <ReactDataGrid
                handle={setGridRef}
                idProperty="index"
                style={gridStyle}
                defaultFilterValue={defaultFilterValue}
                columns={columns}
                dataSource={approvedLibrariesData}
              />
            </Box>
          </Grid>
        </Grid>
      </Box> 
      <FullScreenModal open={openModal} onModalClose={onModalClose} title={'View/Edit Library'}>
        <EditProperty libraryId={libraryInfo.id} onSuccess={onSuccess} onFailure={onModalClose} />
      </FullScreenModal>
    </Paper>
  );
}
 
export default React.memo(ApprovedLibraries);
